import React, { useState } from 'react';
import Axios from 'axios';
import { useFormContext, SubmitHandler, FormProvider, useForm } from 'react-hook-form';
import { Footer } from 'components/common/Modal';
import useCredentialStore from '@/components/admin/integrations/RosteringOverview/AssignmentAndGrades/useCredentialStore';
import { deleteOAuthCredential, updateLtiKey, createLtiKey } from '../queries';

export type LmsType = 'canvas' | 'canvas_1_3' | 'schoology' | 'brightspace';

// Brightspace has no fixed provider id: the backend resolves one per tenant from `brightspace_url`.
const LMS_PROVIDER_ID: Partial<Record<LmsType, number>> = {
  canvas: 1,
  canvas_1_3: 1,
  schoology: 3,
};

interface IFormData {
  lmsType: string;
}

type CredentialsType = {
  id: number;
  subscriber_id: number;
  deployment_id: string;
  client_id?: string;
};

export interface LtiConfigurationProps {
  initialLmsType?: LmsType;
  setCredentials?: Function,
  setLmsType: Function,
  credentials?: CredentialsType,
  previous?: Function;
  next?: Function;
  subscriberId: number;
  lmsType: LmsType;
}

// The wrapped step components (Canvas/Schoology/Brightspace) fix lmsType themselves.
export type LtiStepProps = Omit<LtiConfigurationProps, 'lmsType'>;

interface LtiConfigurationBaseProps extends LtiConfigurationProps {
  disabled: boolean;
  children: ReactChildren;
}

const LtiConfiguration = ({
  subscriberId, next, previous, setLmsType, disabled, children, lmsType, initialLmsType,
}: LtiConfigurationBaseProps) => {
  const ltiCredentials = useCredentialStore(state => state.ltiCredentials);
  const canvasCredentials = useCredentialStore(state => state.canvasCredentials);
  const setStep = useCredentialStore(state => state.setStep);
  const submitting = useCredentialStore(state => state.submitting);
  const setSubmitting = useCredentialStore(state => state.setSubmitting);
  const { handleSubmit } = useFormContext();
  const [serverError, setServerError] = useState<string | null>(null);

  const back = () => {
    setLmsType(null);
    setStep(1);
    previous();
  };

  const submitCallback = () => {
    setStep(2);
    setSubmitting(false);
    next();
  };

  const handleError = (e: { response?: { data?: { errors?: string[] } } }) => {
    setSubmitting(false);
    setServerError(e.response?.data?.errors?.join(' ') || 'Something went wrong. Please try again.');
  };

  const createNewLtiKey = (formData: IFormData) => (
    createLtiKey(subscriberId, { ...formData, subscriber_id: subscriberId, lms_provider_id: LMS_PROVIDER_ID[lmsType] })
      .then(submitCallback).catch(handleError)
  );

  const updateLtiKeyAndDeleteOAuthCredential = (formData: IFormData) => (
    Axios.all([
      updateLtiKey(subscriberId, { ...formData, id: ltiCredentials.id, lms_provider_id: LMS_PROVIDER_ID[lmsType] }),
      deleteOAuthCredential(subscriberId, canvasCredentials.id),
    ]).then(submitCallback).catch(handleError)
  );

  const updateExistingLtiKey = (formData: IFormData) => (
    updateLtiKey(subscriberId, { ...formData, id: ltiCredentials.id, lms_provider_id: LMS_PROVIDER_ID[lmsType] })
      .then(submitCallback).catch(handleError)
  );

  const onSubmit: SubmitHandler<IFormData> = (formData: IFormData) => {
    setServerError(null);
    setSubmitting(true);

    if (!initialLmsType) return createNewLtiKey(formData);
    if (initialLmsType === 'canvas' && lmsType !== 'canvas') return updateLtiKeyAndDeleteOAuthCredential(formData);
    if (initialLmsType !== 'canvas') return updateExistingLtiKey(formData);
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      {children}

      {serverError && <p className="tw-mb-3 tw-text-red" role="alert">{serverError}</p>}

      <Footer
        secondaryButtonText="Back"
        secondaryButtonCallback={back}
        primaryButtonText="Next"
        primaryButtonDisabled={disabled || submitting}
      />
    </form>
  );
};

export const formWrapper = <P extends LtiStepProps>(Component: React.ComponentType<P>) => (props: P) => {
  const methods = useForm({ mode: 'onChange' });

  return (
    <FormProvider {...methods}>
      <Component {...props} />
    </FormProvider>
  );
};

export default LtiConfiguration;
